import React, { FC, useState } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { SearchField, SMBSelect } from '../../../../components';
import { MenuItem, Grid, Button, Divider } from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { ISubcontractorType, IVendorType } from '../../../../models';

interface IPartnersFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  types: ISubcontractorType[];
  selectedTypeId: string;
  setSelectedTypeId: (val: any) => void;
  selectedVendorTypeIds: string[];
  setSelectedVendorTypeIds: (val: any[]) => void;
  vendorTypes: IVendorType[];
  setSearchValue: (val: string) => void;
  setSearchedValue: (val: string) => void;
  searchValue: string;
  handleSearch: (val: string) => void;
  hasAppliedFilters: boolean;
  setHasAppliedFilters: (val: boolean) => void;
}

export const PartnersFilters: FC<IPartnersFilters> = ({
  vendorTypes,
  selectedVendorTypeIds,
  setSelectedVendorTypeIds,
  isLoading,
  applyFilters,
  types,
  selectedTypeId,
  setSelectedTypeId,
  setSearchValue,
  setSearchedValue,
  handleSearch,
  searchValue,
  setHasAppliedFilters,
  hasAppliedFilters
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });
  return (
    <>
      <Grid container spacing={1} alignItems="center" className={classes.searchWrapper}></Grid>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={3}>
          <SearchField
            isDisabled={isLoading}
            handleSearch={handleSearch}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SMBSelect
            SelectProps={{
              multiple: true
            }}
            disabled={isLoading}
            variant="outlined"
            name="Vendor Type"
            label="Vendor Type"
            id="vendorType"
            handleClear={() => setSelectedVendorTypeIds([])}
            value={selectedVendorTypeIds}
            onChange={e => {
              setSelectedVendorTypeIds(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              );
            }}
          >
            {vendorTypes &&
              vendorTypes.map((type, index) => {
                return (
                  <MenuItem key={`${index}`} value={type.vendorTypeId}>
                    {type.name}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SMBSelect
            disabled={isLoading}
            name="type"
            label="Subcontractor Types"
            id="type"
            variant="outlined"
            value={selectedTypeId}
            handleClear={() => setSelectedTypeId('')}
            onChange={({ target: { value } }) => setSelectedTypeId(Number(value))}
          >
            {types &&
              types.map((type, index) => {
                return (
                  <MenuItem key={`${index}`} value={type.subcontractorTypeId}>
                    {type.name}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setSearchedValue(searchValue);
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              color="inherit"
              disabled={isLoading}
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSelectedTypeId('');
                setSelectedVendorTypeIds([]);
                setSearchedValue('');
                setSearchValue('');
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex'
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  },

  searchWrapper: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      marginBottom: theme.spacing(1)
    }
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: theme.spacing(1)
    }
  },
  searchButton: {
    minWidth: '8rem'
  }
}));
